// ═══════════════════════════════════════════════════════════════════════════
//  KALI SECURITY STRIP (God 2026-08-26)
//
//  Sits above the trading UI on the KALI page, COLLAPSED by default. God's
//  correction was right: the security console was displacing the page instead of
//  serving it. KALI must be the DUMABATI page — same cards, same accordion, same
//  Open Positions and Execution Console — with the gates one click away rather
//  than in the way.
//
//  What stays visible even when collapsed is the single fact that matters on a
//  real-money page: is anything ARMED. That is not hidden behind a click,
//  because a page that can spend money should never make you look for that.
//
//  This panel SHOWS and can STOP. It cannot arm: approval needs X-God-Key
//  against GOD_APPROVAL_TOKEN, which no browser holds — so no UI bug and no
//  stolen session can put money at risk. DEMOTE and DISARM-ALL are here, because
//  stopping must always be easier than starting.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE as API } from '../lib/api';

type Promotion = {
  cell: string; bot_id: string; symbol: string; timeframe: string;
  status: string; active: boolean; max_qty: number; approved_by?: string;
  evidence?: { trades?: number; spanHours?: number };
};

const RED = '#ef4444', DIM = '#64748b', OK = '#34d399', WARN = '#fbbf24';

const adminHeaders = (): Record<string, string> => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const k = localStorage.getItem('jaiswal_admin_key');
    if (k) h['X-Admin-Key'] = k;
  } catch { /* private mode */ }
  return h;
};

const Gate: React.FC<{ n: number; name: string; open: boolean; detail: string }> =
  ({ n, name, open, detail }) => (
    <div style={{
      padding: '7px 9px', borderRadius: 6,
      background: open ? 'rgba(239,68,68,.07)' : 'rgba(52,211,153,.05)',
      border: `1px solid ${open ? 'rgba(239,68,68,.3)' : 'rgba(52,211,153,.2)'}`,
    }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: open ? RED : OK }}>
        {open ? '🔴' : '🛡️'} GATE {n} · {name} · {open ? 'OPEN' : 'CLOSED'}
      </div>
      <div style={{ fontSize: 8.5, color: DIM, marginTop: 2, lineHeight: 1.45 }}>{detail}</div>
    </div>
  );

export const KaliSecurityPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Promotion[]>([]);
  const [armed, setArmed] = useState(0);
  const [godKey, setGodKey] = useState<boolean | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/kali/promotions?t=${Date.now()}`, { cache: 'no-store' });
      const d = await r.json();
      setRows(d.promotions || []); setArmed(Number(d.armed) || 0);
      setGodKey(!!d.godKeyConfigured); setErr('');
    } catch (e: any) { setErr(String(e?.message || e)); }
  }, []);

  useEffect(() => { void load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const act = async (path: string, body: any, ask: string) => {
    if (!window.confirm(ask)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, { method: 'POST', headers: adminHeaders(), body: JSON.stringify(body) });
      const d = await r.json();
      if (d.status !== 'ok') window.alert(`Failed: ${d.message || 'unknown'}`);
      await load();
    } catch (e: any) { window.alert(`Failed: ${e?.message || e}`); }
    setBusy(false);
  };

  const SNIPPET = `{
  "auth_token": "<KALI_WEBHOOK_TOKEN>",
  "bot_id":     "mai_veg",
  "symbol":     "NIFTY_50",
  "timeframe":  "5m",
  "action":     "BUY",
  "quantity":   50,
  "sig_kind":   "entry"
}`;

  return (
    <div style={{ background: '#12080a', borderBottom: `1px solid ${RED}44` }}>
      {/* Always-visible bar. The armed count never hides behind a click. */}
      <div
        onClick={() => setOpen(o => !o)}
        role="button" tabIndex={0} aria-expanded={open}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 20px', cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color: open ? '#fca5a5' : DIM, width: 9, display: 'inline-block',
                       transition: 'transform .12s', transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
        <span style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 1, color: RED }}>KALI SECURITY</span>
        <span style={{
          fontSize: 9, fontWeight: 900, padding: '2px 8px', borderRadius: 4,
          background: armed > 0 ? RED : 'rgba(52,211,153,.12)', color: armed > 0 ? '#fff' : OK,
        }}>
          {armed > 0 ? `${armed} CELL${armed === 1 ? '' : 'S'} ARMED` : 'NOTHING ARMED'}
        </span>
        {err
          ? <span style={{ fontSize: 8.5, color: WARN }}>registry unreadable — guard treats it as EMPTY, so no entry can be placed</span>
          : <span style={{ fontSize: 8.5, color: DIM }}>gates · promotions · webhook setup</span>}
        {armed > 0 && (
          <button
            onClick={e => { e.stopPropagation(); void act('/api/kali/disarm-all', { by: 'ui' },
              'DISARM ALL armed cells?\n\nNo new real entries.\nOpen positions are unaffected and can still be exited.'); }}
            disabled={busy}
            style={{ marginLeft: 'auto', fontSize: 8.5, fontWeight: 900, padding: '3px 10px', borderRadius: 4,
                     cursor: 'pointer', background: 'rgba(239,68,68,.18)', border: `1px solid ${RED}`, color: RED }}>
            🛑 DISARM ALL
          </button>
        )}
      </div>

      {open && (
        <div style={{ padding: '4px 20px 14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 235px), 1fr))', gap: 7, marginBottom: 12 }}>
            <Gate n={1} name="PROCESS" open={false}
                  detail="TRADING_MODE=live on kali-executor only. Held closed until credentials are separated." />
            <Gate n={2} name="CREDENTIALS" open={false}
                  detail="Order path reads KALI_DHAN_* only. Unset — no order-capable client can be built." />
            <Gate n={3} name="EXPLICIT MODE" open
                  detail="Enforced. An empty mode is never treated as live." />
            <Gate n={4} name="PROMOTION" open={armed > 0}
                  detail={godKey === false
                    ? 'Registry EMPTY; GOD_APPROVAL_TOKEN unset, so nothing can be added.'
                    : `${armed} cell(s) armed. Approval needs X-God-Key, which no browser holds.`} />
            <Gate n={5} name="SIZE CEILING" open
                  detail="Enforced. A promotion without max_qty opens nothing." />
          </div>

          {rows.length > 0 && (
            <div style={{ marginBottom: 12, border: '1px solid #1e293b', borderRadius: 6, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9.5 }}>
                <thead><tr>{['CELL', 'STATUS', 'MAX QTY', 'EVIDENCE', ''].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 2 && i < 4 ? 'right' : 'left', padding: '5px 9px', fontSize: 8,
                                       fontWeight: 800, letterSpacing: 1, color: DIM, background: '#0d1117' }}>{h}</th>))}
                </tr></thead>
                <tbody>{rows.map(r => (
                  <tr key={r.cell}>
                    <td style={{ padding: '5px 9px', borderTop: '1px solid #131a2e', fontFamily: 'monospace', color: r.active ? '#e2e8f0' : DIM }}>{r.cell}</td>
                    <td style={{ padding: '5px 9px', borderTop: '1px solid #131a2e', color: r.active ? RED : DIM, fontWeight: 800 }}>{r.active ? 'ARMED' : r.status}</td>
                    <td style={{ padding: '5px 9px', borderTop: '1px solid #131a2e', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.max_qty || '—'}</td>
                    <td style={{ padding: '5px 9px', borderTop: '1px solid #131a2e', textAlign: 'right', color: DIM }}>
                      {r.evidence ? `${r.evidence.trades ?? 0}t · ${r.evidence.spanHours ?? 0}h` : '—'}</td>
                    <td style={{ padding: '5px 9px', borderTop: '1px solid #131a2e' }}>
                      {r.active && (
                        <button onClick={() => act('/api/kali/demote', { bot_id: r.bot_id, symbol: r.symbol, timeframe: r.timeframe, by: 'ui', reason: 'demoted from KALI page' },
                          `Demote ${r.cell}?\n\nNew entries stop within ~20s.\nOpen positions can still be exited.`)}
                          disabled={busy}
                          style={{ fontSize: 8, fontWeight: 900, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                                   background: 'rgba(251,191,36,.12)', border: `1px solid ${WARN}66`, color: WARN }}>DEMOTE</button>
                      )}
                    </td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}

          {/* WEBHOOK SETUP — the URL, port and token name are exactly what gets
              mis-copied, and a mis-copied LIVE alert goes nowhere or lands wrong. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 290px), 1fr))', gap: 10 }}>
            <div>
              <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1, color: DIM, marginBottom: 5 }}>WEBHOOK ENDPOINTS — nothing shared</div>
              <div style={{ padding: '7px 9px', borderRadius: 6, background: 'rgba(148,163,184,.05)', border: '1px solid #1e293b', marginBottom: 6 }}>
                <div style={{ fontSize: 8, fontWeight: 800, color: DIM }}>📄 DUMABATI · PAPER</div>
                <code style={{ fontSize: 9.5, color: '#7dd3fc', wordBreak: 'break-all' }}>http://127.0.0.1:5001/webhook/tv</code>
              </div>
              <div style={{ padding: '7px 9px', borderRadius: 6, background: 'rgba(239,68,68,.06)', border: `1px solid ${RED}44` }}>
                <div style={{ fontSize: 8, fontWeight: 800, color: RED }}>🔱 LIVE AUTO TRADING KALI</div>
                <code style={{ fontSize: 9.5, color: '#fca5a5', wordBreak: 'break-all' }}>http://127.0.0.1:5002/kali/webhook/tv</code>
                <div style={{ fontSize: 8, color: DIM, marginTop: 3 }}>
                  loopback only — not proxied, not reachable from the internet. Auth via <code>KALI_WEBHOOK_TOKEN</code>.
                </div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1, color: DIM }}>KALI ALERT PAYLOAD</span>
                <button onClick={() => { navigator.clipboard?.writeText(SNIPPET); setCopied(true); setTimeout(() => setCopied(false), 1400); }}
                  style={{ fontSize: 8, fontWeight: 800, padding: '1px 7px', borderRadius: 4, cursor: 'pointer',
                           background: copied ? 'rgba(52,211,153,.15)' : 'rgba(148,163,184,.1)',
                           border: `1px solid ${copied ? 'rgba(52,211,153,.5)' : 'rgba(148,163,184,.3)'}`,
                           color: copied ? OK : '#94a3b8' }}>{copied ? 'COPIED' : 'COPY'}</button>
              </div>
              <pre style={{ margin: 0, padding: '8px 10px', background: '#0b1220', border: '1px solid #1e293b',
                            borderRadius: 6, fontSize: 9.5, lineHeight: 1.55, color: '#cbd5e1', overflowX: 'auto',
                            fontFamily: 'ui-monospace, monospace' }}>{SNIPPET}</pre>
              <div style={{ fontSize: 8, color: DIM, marginTop: 4, lineHeight: 1.5 }}>
                <code>mode</code> is not sent — the endpoint stamps LIVE itself, so an alert can never
                claim its own lane. The token is <strong>not</strong> GOD_APPROVAL_TOKEN: a leaked alert
                body must not become the ability to arm cells.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KaliSecurityPanel;
